import { useState, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PAD = 8;
const HIDE_DELAY_MS = 150;

export default function HoverPopover({ trigger, children, disabled = false }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  }, [clearHideTimer]);

  const show = useCallback(() => {
    if (disabled) return;
    clearHideTimer();
    setOpen(true);
  }, [disabled, clearHideTimer]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const panelEl = panelRef.current;
    const panelWidth = Math.min(280, panelEl?.offsetWidth || 280);
    const panelHeight = panelEl?.offsetHeight || 120;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp =
      spaceBelow < panelHeight + VIEWPORT_PAD && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - panelHeight - 6 : rect.bottom + 6;
    let left = rect.left;

    left = Math.max(
      VIEWPORT_PAD,
      Math.min(left, window.innerWidth - panelWidth - VIEWPORT_PAD),
    );
    top = Math.max(
      VIEWPORT_PAD,
      Math.min(top, window.innerHeight - panelHeight - VIEWPORT_PAD),
    );

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition, children]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        {trigger}
      </span>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[10050] max-w-[280px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg"
            style={{ top: pos.top, left: pos.left }}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            role="tooltip"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
